import type { NotificationReaderContext } from '@/modules/notifications/reads.api'
import { useMemo } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'

import { SessionChangedError } from '@scrambl/shared/session-scope'

/** Portal notifications have their own entity-scoped surface and transport. */
export function useNotificationReadSession() {
  const workspace = useWorkspaceSession()
  const portalEntityId = getCurrentClientPortalContext()?.entityId ?? null
  return useMemo(() => {
    const enabled =
      workspace.enabled &&
      !!workspace.organizationId &&
      !!workspace.ownerUserId &&
      portalEntityId === null
    const assertCurrent = () => {
      workspace.assertUnchanged()
      if (
        !enabled ||
        workspace.signal.aborted ||
        getCurrentClientPortalContext()
      )
        throw new SessionChangedError()
    }
    return {
      enabled,
      ownerUserId: workspace.ownerUserId,
      assertUnchanged: workspace.assertUnchanged,
      organizationId: workspace.organizationId,
      key: { workspaceSession: workspace.key, portalEntityId },
      assertCurrent,
      run<T>(
        signal: AbortSignal,
        action: (context: NotificationReaderContext) => Promise<T>,
      ): Promise<T> {
        return workspace.run(signal, async linked => {
          assertCurrent()
          const result = await action({
            organizationId: workspace.organizationId!,
            userId: workspace.ownerUserId!,
            signal: linked,
          })
          assertCurrent()
          return result
        })
      },
    }
  }, [workspace, portalEntityId])
}
